import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { apiService } from '../services/api';

type ForgotPasswordDialogProps = {
  open: boolean;
  onClose: () => void;
};

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({ open, onClose }) => {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [resetPath, setResetPath] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    if (loading) return;
    setIdentifier('');
    setMessage('');
    setResetPath('');
    setError('');
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    setResetPath('');
    try {
      const response = await apiService.forgotPassword(identifier.trim());
      const data = response.data?.data;
      setMessage(response.data?.message || 'If an account exists, a reset link has been sent.');
      if (data?.resetPath) setResetPath(data.resetPath);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not start password reset.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset password</DialogTitle>
      <BoxForm onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            required
            fullWidth
            label="Username or email"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            disabled={loading}
            sx={{ mt: 1 }}
          />
          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
              {resetPath && (
                <>
                  {' '}
                  Email is not configured in this environment.{' '}
                  <a href={resetPath}>Open the reset link</a>
                </>
              )}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Close</Button>
          <Button type="submit" variant="contained" disabled={loading || !identifier.trim()}>
            {loading ? 'Sending…' : 'Send reset link'}
          </Button>
        </DialogActions>
      </BoxForm>
    </Dialog>
  );
};

const BoxForm: React.FC<{ onSubmit: (event: React.FormEvent) => void; children: React.ReactNode }> = ({ onSubmit, children }) => (
  <form onSubmit={onSubmit}>{children}</form>
);

export default ForgotPasswordDialog;
